import React, { Component } from "react";

import { withRouter } from "react-router-dom";

import ProductInfo from "./ProductInfo";
import ProductOptions from "./ProductOptions";
import ProductDays from "./ProductDays";
import ProductSalesInformations from "./ProductSalesInformations";
import ProductPrices from "./ProductPrices";

import { AppContext } from "../../App";
import Loader from "../../components-utils/Loader";
import PhotoHeader from "../../components-utils/PhotoHeader";
import I18n from "../../components-utils/I18n";
import {
  validateObject,
  mandatory,
  number,
  numberMin,
  numberBetween
} from "../../services/validator";
import { loadItem } from "../../services/utils";

import {
  getProduct,
  uploadProductPhoto,
  deleteProductPhoto,
  getPriceGrid,
  getPeriods,
  saveProduct
} from "../../services/api";

import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardActions from "@material-ui/core/CardActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";

import "./Product.css";

const emptyI18n = {
  label: "",
  description: ""
};
const emptyProduct = (merchantCode, langs) => ({
  id: -1,
  merchantCode: merchantCode,
  productCategoryId: "",
  productTypeId: 1,
  displayed: false,
  rank: 0,
  value: {
    technicalLabel: "",
    season: "",
    minAge: "",
    maxAge: "",
    type: "",
    i18n: langs.reduce((acc, lang) => ({ ...acc, [lang.code]: emptyI18n }), {})
  },
  options: [],
  salesInformations: [],
  unavailabilities: []
});

const initPrices = (product, grid, periods) => ({
  ...product,
  value: {
    ...product.value,
    prices: {
      grid: grid.reduce((acc, p) => ({ ...acc, [p.id]: "" }), {}),
      periods: periods.reduce((acc, p) => ({ ...acc, [p.id]: "" }), {})
    }
  }
});

class Product extends Component {
  state = {
    lang: "fr",
    errors: {},
    randomSuffix: Math.random()
  };

  async componentDidMount() {
    const result = await loadItem(this.props, getProduct, emptyProduct);

    if (result !== undefined) {
      const grid = await getPriceGrid(result.merchantCode);
      const periods = await getPeriods(result.merchantCode);

      const product =
        grid === undefined || periods === undefined
          ? undefined
          : result.item.id === -1
          ? initPrices(result.item, grid, periods)
          : result.item;

      this.setState({
        product: {
          ...product,
          value: {
            ...product.value,
            minAge: product.value.minAge || "",
            maxAge: product.value.maxAge || "",
            type: product.value.type || ""
          }
        },
        cantAdd: product === undefined,
        merchantCode: result.merchantCode,
        merchantKind: result.merchantKind,
        grid,
        periods
      });
    }
  }

  changeLang = lang => this.setState({ lang });

  changeI18n = name => e => {
    const value = e.target.value;
    this.setState(state => ({
      product: {
        ...state.product,
        value: {
          ...state.product.value,
          i18n: {
            ...state.product.value.i18n,
            [state.lang]: {
              ...state.product.value.i18n[state.lang],
              [name]: value
            }
          }
        }
      }
    }));
  };

  changeFlatInfo = (name, value) =>
    this.setState(state => ({
      product: {
        ...state.product,
        [name]: value
      }
    }));

  changeJsonInfo = (name, value, type = "string") => {
    const typedValue = type === "number" ? parseInt(value, 10) : value;
    this.setState(state => ({
      product: {
        ...state.product,
        value: {
          ...state.product.value,
          [name]: isNaN(typedValue) ? value : typedValue
        }
      }
    }));
  };

  changeInfo = (name, inJson, value, type) => {
    inJson
      ? this.changeJsonInfo(name, value, type)
      : this.changeFlatInfo(name, value);
  };

  changePrice = (type, name, value) =>
    this.setState(state => ({
      product: {
        ...state.product,
        value: {
          ...state.product.value,
          prices: {
            ...state.product.value.prices,
            [type]: {
              ...state.product.value.prices[type],
              [name]: value
            }
          }
        }
      }
    }));

  changeLinkedItem = params => (id, name, value) =>
    this.setState(state => ({
      product: {
        ...state.product,
        [params]: state.product[params].map(param =>
          param.id === id ? { ...param, [name]: value } : param
        )
      }
    }));

  addLinkedItem = (params, attrs) => id =>
    this.setState(state => ({
      product: {
        ...state.product,
        [params]: [
          ...state.product[params],
          {
            id,
            ...attrs,
            productId: state.product.id
          }
        ]
      }
    }));

  deleteLinkedItem = params => id => () =>
    this.setState(state => ({
      product: {
        ...state.product,
        [params]: state.product[params].filter(param => param.id !== id)
      }
    }));
  deleteMultiple = ids => {
    this.setState(state => ({
      product: {
        ...state.product,
        salesInformations: state.product.salesInformations.filter(
          si => !ids.some(id => si.id === id)
        )
      }
    }));
  };

  uploadPhoto = async file => {
    const { snackbar } = this.props;
    const {
      merchantCode,
      product: { id }
    } = this.state;

    try {
      const product = await uploadProductPhoto(id, merchantCode, file);
      this.setState(
        state => ({ product, randomSuffix: Math.random() }),
        () => snackbar("Photo enregistrée")
      );
    } catch (e) {
      snackbar(
        "Une erreur est survenue pendant l'enregistrement de la photo, veuillez réessayer",
        "error"
      );
    }
  };
  deletePhoto = async () => {
    const { snackbar } = this.props;
    const {
      merchantCode,
      product: { id }
    } = this.state;
    try {
      const product = await deleteProductPhoto(id, merchantCode);
      this.setState({ product }, () => snackbar("Photo supprimée"));
    } catch (e) {
      snackbar(
        "Une erreur est survenue pendant la suppression, veuillez réessayer",
        "error"
      );
    }
  };

  pricesValidator = (object, validators) =>
    object.reduce((acc, price) => ({ ...acc, [price.id]: validators }), {});

  productValidators = (checkPrices, checkAgesAndType) => ({
    productCategoryId: [mandatory],
    value: {
      season: [mandatory],
      technicalLabel: [mandatory],
      minAge: checkAgesAndType ? [mandatory] : undefined,
      maxAge: checkAgesAndType ? [mandatory] : undefined,
      type: checkAgesAndType ? [mandatory] : undefined,
      i18n: this.props.langs.reduce(
        (acc, lang) => ({ ...acc, [lang.code]: { label: [mandatory] } }),
        {}
      ),
      prices: checkPrices
        ? {
            grid: this.pricesValidator(this.state.grid, [
              mandatory,
              number,
              numberMin(0)
            ]),
            periods: this.pricesValidator(this.state.periods, [
              mandatory,
              number,
              numberBetween(0, 100)
            ])
          }
        : undefined
    },
    options: {
      element: {
        optionId: [mandatory]
      }
    },
    salesInformations: {
      element: {
        salesInformationId: [mandatory]
      }
    }
  });

  saveProduct = () => {
    const { product } = this.state;
    const checkPrices =
      product.options.length === 0 ||
      !product.options.some(option => option.mandatory);
    const checkAgesAndType = product.displayed;

    const errors = validateObject(
      product,
      this.productValidators(checkPrices, checkAgesAndType)
    );

    this.setState({ errors }, () => {
      const { errors, product, merchantCode } = this.state;
      const { snackbar } = this.props;
      if (errors === undefined) {
        const {
          history,
          match: {
            params: { id }
          }
        } = this.props;
        if (merchantCode !== undefined) {
          saveProduct(id, merchantCode, product)
            .then(product => {
              this.setState({ product }, () => {
                history.push(`/products/${product.id}`);
                snackbar("Informations enregistrées");
              });
            })
            .catch(e => {
              console.log(e);
              snackbar(
                "Erreur pendant la sauvegarde du produit. Veuillez réessayer",
                "error"
              );
            });
        }
      } else {
        snackbar(
          "Votre produit contient des erreurs. Veuillez les corriger, puis enregistrer à nouveau",
          "error"
        );
      }
    });
  };

  renderLoader() {
    return <Loader loading={true} />;
  }

  renderErrorMessage() {
    return (
      <Typography variant="body2">
        Vous ne pouvez pas créer de produits tant que vous n'avez pas créé au
        moins une période et un prix.
      </Typography>
    );
  }

  render() {
    const {
      product,
      lang,
      merchantCode,
      merchantKind,
      randomSuffix,
      grid,
      periods,
      errors,
      cantAdd
    } = this.state;
    const merchantStandardProducts = merchantKind === "STAND";
    return product !== undefined ? (
      <Card key="page">
        <PhotoHeader
          disabled={product !== undefined && product.id <= 0}
          photo={
            product !== undefined && product.value !== undefined
              ? product.value.photo
              : undefined
          }
          path={`public/${merchantCode}/products`}
          uploadPhoto={this.uploadPhoto}
          deletePhoto={this.deletePhoto}
          snackbar={this.props.snackbar}
          randomSuffix={randomSuffix}
        />
        <CardContent>
          <Typography variant="h4">
            {product && product.value && product.value.technicalLabel
              ? product.value.technicalLabel
              : "Nouveau Produit"}
          </Typography>
          <ProductInfo
            merchantCode={merchantCode}
            merchantKind={merchantKind}
            product={product}
            changeInfo={this.changeInfo}
            errors={errors}
          />
          {merchantStandardProducts ? (
            <ProductDays
              days={product.value.days}
              changeDays={this.changeJsonInfo}
            />
          ) : null}
          <Divider className="sp-product--divider" light />
          <I18n
            langs={this.props.langs}
            lang={lang}
            changeFields={this.changeI18n}
            changeLang={this.changeLang}
            elements={product.value.i18n}
            emptyElement={emptyI18n}
            errors={errors && errors.value && errors.value.i18n}
          />
          <Divider className="sp-product--divider" light />
          <ProductOptions
            merchantCode={merchantCode}
            productId={product.id}
            productOptions={product.options}
            changeOption={this.changeLinkedItem("options", "optionId")}
            deleteOption={this.deleteLinkedItem("options", "optionId")}
            addOption={this.addLinkedItem("options", {
              optionId: "",
              precheck: false,
              mandatory: false
            })}
            errors={errors && errors.options}
          />
          <Divider className="sp-product--divider" light />
          <ProductSalesInformations
            merchantCode={merchantCode}
            productOptions={product.options}
            productSalesInformations={product.salesInformations}
            changeSalesInformation={this.changeLinkedItem(
              "salesInformations",
              "salesInformationId"
            )}
            deleteSalesInformation={this.deleteLinkedItem("salesInformations")}
            addSalesInformation={this.addLinkedItem("salesInformations", {
              salesInformationId: "",
              mandatory: false
            })}
            deleteMultiple={this.deleteMultiple}
            errors={errors && errors.salesInformations}
          />
          <Divider className="sp-product--divider" light />
          <ProductPrices
            merchantCode={merchantCode}
            prices={product.value.prices}
            changePrice={this.changePrice}
            errors={errors && errors.value && errors.value.prices}
            grid={grid}
            periods={periods}
          />
        </CardContent>
        <CardActions className="sp-product--actions">
          <Button
            variant="contained"
            color="secondary"
            onClick={this.saveProduct}
          >
            Valider
          </Button>
        </CardActions>
      </Card>
    ) : cantAdd ? (
      this.renderErrorMessage()
    ) : (
      this.renderLoader()
    );
  }
}

const enhanced = WrappedComponent => props => {
  return (
    <AppContext.Consumer>
      {app => {
        return (
          <WrappedComponent
            {...props}
            snackbar={app.snackbar.open}
            langs={app.langs}
          />
        );
      }}
    </AppContext.Consumer>
  );
};

export default withRouter(enhanced(Product));



// WEBPACK FOOTER //
// ./src/components/products/Product.js